// Configure Laptop workbench — scan a laptop passport, install/replace
// RAM/SSD/Battery/Adapter by scanning their passports. Uses the
// /passports/{parent}/components endpoints from Commit 2A.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorToMessage } from "@/lib/apiClient";
import PassportPicker from "@/components/PassportPicker";

const CHILD_TYPES = ["ram", "ssd", "battery", "adapter"];

// Only truly unassembled / QC-passed items can be installed.
const CHILD_ELIGIBLE = new Set(["AVAILABLE", "QC_PASSED", "READY_FOR_SALE"]);

export default function ConfigureLaptopPage() {
  const [laptop, setLaptop] = useState(null);         // {id, passport_uid, ...}
  const [active, setActive] = useState([]);           // active components
  const [historic, setHistoric] = useState([]);       // historic components
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(null); // {child, notes}
  const [replaceTarget, setReplaceTarget] = useState(null); // link_id being replaced

  const loadComponents = useCallback(async (laptopId) => {
    const { data } = await api.get(`/passports/${laptopId}/components`);
    setActive(data.active || []);
    setHistoric(data.historic || []);
  }, []);

  useEffect(() => {
    if (!laptop) return;
    loadComponents(laptop.id).catch((e) => setError(errorToMessage(e, "Load failed")));
  }, [laptop, loadComponents]);

  const scanLaptop = async (p) => {
    if (p.item_type !== "laptop") {
      setError("Please scan a LAPTOP passport (got " + p.item_type + ")");
      return;
    }
    setError(null);
    setLaptop(p);
  };

  const doInstall = async (child, previousLinkId = null) => {
    setBusy(true); setError(null);
    try {
      if (previousLinkId) {
        await api.delete(`/passports/${laptop.id}/components/${previousLinkId}`);
      }
      await api.post(`/passports/${laptop.id}/components`, {
        child_passport_id: child.id, relationship_type: "INSTALLED_IN",
      });
      await loadComponents(laptop.id);
      setInstalling(null); setReplaceTarget(null);
    } catch (e) { setError(errorToMessage(e, "Install failed")); }
    finally { setBusy(false); }
  };

  const doRemove = async (linkId) => {
    if (!window.confirm("Remove this component? Its history is preserved.")) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/passports/${laptop.id}/components/${linkId}`);
      await loadComponents(laptop.id);
    } catch (e) { setError(errorToMessage(e, "Remove failed")); }
    finally { setBusy(false); }
  };

  const childFilter = (it) => {
    if (!CHILD_TYPES.includes(it.meta?.item_type)) return false;
    if (!CHILD_ELIGIBLE.has(it.meta?.status)) return false;
    // Cannot install a passport that is already actively linked somewhere.
    if (active.some((c) => c.child_passport_id === it.id)) return false;
    return true;
  };

  return (
    <section className="p-6 space-y-4" data-testid="configure-laptop-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Configure Laptop</h1>
        <p className="text-sm text-slate-500">
          Assemble a laptop by scanning component Passport IDs. Component
          relationships are recorded permanently on the Digital Passport.
        </p>
      </header>

      {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      {/* Step 1 — scan laptop */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
          Step 1 — Scan the laptop
        </div>
        {laptop ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-lg font-semibold text-blue-700">
                {laptop.passport_uid}
              </div>
              <div className="text-xs text-slate-600">{laptop.title} · {laptop.status}</div>
            </div>
            <button onClick={() => { setLaptop(null); setActive([]); setHistoric([]); }}
                    className="text-xs text-slate-500 hover:underline">
              Change laptop
            </button>
          </div>
        ) : (
          <PassportPicker
            onPick={scanLaptop}
            filterFn={(it) => it.meta?.item_type === "laptop"}
            placeholder="Scan / type LAPTOP Passport ID"
            testId="cfg-laptop-picker" />
        )}
      </div>

      {laptop && (
        <>
          {/* Step 2 — installed components */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
              <div className="text-sm font-semibold text-slate-800">Installed Components ({active.length})</div>
              <Link to={`/admin/passports/${laptop.id}`}
                    className="text-xs text-blue-600 hover:underline">Open Digital Passport →</Link>
            </div>

            {active.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No components installed. Add RAM, SSD, Battery or Adapter below.
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Passport</th>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-left">Installed</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100"
                        data-testid={`cfg-active-${c.passport_uid}`}>
                      <td className="px-3 py-2 font-mono text-blue-700">{c.passport_uid}</td>
                      <td className="px-3 py-2 text-xs uppercase text-indigo-700">{c.item_type}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {(c.brand || c.model) ? `${c.brand || ""} ${c.model || ""}`.trim() : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {c.linked_at?.slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setReplaceTarget(c)}
                                data-testid={`cfg-replace-${c.passport_uid}`}
                                className="mr-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                          Replace
                        </button>
                        <button onClick={() => doRemove(c.id)}
                                data-testid={`cfg-remove-${c.passport_uid}`}
                                className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Step 3 — install / replace */}
          {(installing || replaceTarget) ? (
            <InstallPanel
              laptop={laptop} target={replaceTarget}
              onCancel={() => { setInstalling(null); setReplaceTarget(null); }}
              onConfirm={(child) => doInstall(child, replaceTarget?.id || null)}
              childFilter={childFilter}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                Step 3 — Install another component
              </div>
              <button onClick={() => setInstalling({})}
                      data-testid="cfg-install-btn"
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                + Scan Component to Install
              </button>
            </div>
          )}

          {/* Historic */}
          {historic.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-slate-800">
                History ({historic.length}) — append-only record
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Passport</th>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Installed</th>
                    <th className="px-3 py-2 text-left">Removed</th>
                  </tr>
                </thead>
                <tbody>
                  {historic.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-slate-600">{c.passport_uid}</td>
                      <td className="px-3 py-2 text-xs uppercase text-slate-500">{c.item_type}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{c.linked_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{c.unlinked_at?.slice(0, 19).replace("T", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}


function InstallPanel({ laptop, target, onCancel, onConfirm, childFilter }) {
  const [picked, setPicked] = useState(null);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="cfg-install-panel">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-blue-800">
        {target
          ? `Replace component ${target.passport_uid} on ${laptop.passport_uid}`
          : `Install component into ${laptop.passport_uid}`}
      </div>
      {picked ? (
        <div className="flex items-center justify-between rounded-md border border-blue-300 bg-white p-3">
          <div>
            <div className="font-mono text-lg font-semibold text-blue-700">{picked.passport_uid}</div>
            <div className="text-xs text-slate-700">{picked.title} · {picked.item_type}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-white">
              Cancel
            </button>
            <button onClick={() => onConfirm(picked)}
                    data-testid="cfg-install-confirm"
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700">
              {target ? "Replace" : "Install"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <PassportPicker onPick={setPicked} filterFn={childFilter}
                           testId="cfg-child-picker"
                           placeholder="Scan RAM / SSD / Battery / Adapter Passport ID" />
          <div className="mt-2 text-right">
            <button onClick={onCancel} className="text-xs text-slate-600 hover:underline">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
